import React from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronDown, Plus, Minus, X, Search, Loader2, MapPin } from "lucide-react";
import { createContext, useContext, useState, useEffect, useRef } from "react";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyB3FJ-Iy1zR_Xl2WRz11X-rUg--0HkW1Lo";

// --- PlaceSearch ---
export const PlaceSearch = ({ label, value = "", locationBias, placeholder = "Search for a business or address...", onSelect, required, isLight = false, showValidation: propShowValidation }) => {
  const { showValidation: contextShowValidation } = useFormContext();
  const showValidation = propShowValidation || contextShowValidation;
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [sessionToken, setSessionToken] = useState("");
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    setSessionToken(crypto.randomUUID());
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (val) => {
    setQuery(val);
    if (!val || val.length < 3) {
      setSuggestions([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const body = {
          input: val,
          sessionToken: sessionToken,
          includedPrimaryTypes: ['establishment', 'street_address'],
          languageCode: 'en',
          includedRegionCodes: ['gr'] // Prioritize Greece
        };

        // Add location bias if coordinates are provided
        if (locationBias && locationBias.lat && locationBias.lon) {
          body.locationBias = {
            circle: {
              center: { latitude: locationBias.lat, longitude: locationBias.lon },
              radius: 5000.0 // 5km bias
            }
          };
        } else {
          // Default bias to Athens center if no specific location
          body.locationBias = {
            circle: {
              center: { latitude: 37.9838, longitude: 23.7275 },
              radius: 10000.0 // 10km bias
            }
          };
        }

        const res = await fetch(`https://places.googleapis.com/v1/places:autocomplete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Goog-Api-Key': GOOGLE_API_KEY },
          body: JSON.stringify(body)
        });
        const data = await res.json();
        if (data.suggestions) {
          setSuggestions(data.suggestions.map(s => ({
            placeId: s.placePrediction?.placeId,
            displayName: s.placePrediction?.text?.text,
            mainText: s.placePrediction?.structuredFormat?.mainText?.text,
            secondaryText: s.placePrediction?.structuredFormat?.secondaryText?.text
          })));
        } else {
          setSuggestions([]);
        }
      } catch (e) {
        console.error("Autocomplete error", e);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  };

  const handleSelect = async (suggestion) => {
    setQuery(suggestion.mainText);
    setSuggestions([]);
    setIsSearching(true);
    try {
      const res = await fetch(`https://places.googleapis.com/v1/places/${suggestion.placeId}?fields=displayName,formattedAddress,location,types,regularOpeningHours&languageCode=en`, {
        headers: { 'X-Goog-Api-Key': GOOGLE_API_KEY }
      });
      const data = await res.json();
      onSelect({
        name: data.displayName?.text || suggestion.mainText,
        address: data.formattedAddress,
        lat: data.location?.latitude,
        lon: data.location?.longitude,
        types: data.types,
        hours: data.regularOpeningHours?.weekdayDescriptions?.join(", ")
      });
      setSessionToken(crypto.randomUUID());
    } catch (e) {
      console.error("Details error", e);
    } finally {
      setIsSearching(false);
    }
  };

  const isInvalid = showValidation && required && !value;

  return (
    <div className="flex flex-col w-full relative" ref={containerRef}>
      {label && <label className={labelBase(isLight)}>{label} {required && <span className="text-red-500">*</span>}</label>}
      <div className={`flex items-center gap-3 border rounded-xl px-4 py-2.5 transition-all ${
        isLight ? "bg-white border-neutral-300 focus-within:border-neutral-900" : "bg-[#05070A]/60 border-white/10 focus-within:border-white"
      } ${isInvalid ? "border-red-500 bg-red-500/5 ring-2 ring-red-500/20" : ""}`}>
        <Search size={18} className={isLight ? "text-neutral-400" : "text-neutral-500"} />
        <input 
          type="text"
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-neutral-500"
          placeholder={placeholder}
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          onBlur={() => {
            // If the user typed something but didn't select, sync it to parent as manual text
            if (query && !value) {
              onSelect({ address: query, lat: null, lon: null, name: query });
            }
          }}
        />
        {isSearching && <Loader2 size={16} className="animate-spin opacity-50" />}
      </div>
      {isInvalid && <span className="text-[10px] font-bold text-red-500 mt-1.5 uppercase tracking-wider">Please select an address from the suggestions</span>}

      <AnimatePresence>
        {suggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className={`absolute top-full left-0 right-0 mt-2 rounded-2xl shadow-2xl border p-2 z-[100] overflow-hidden flex flex-col max-h-[300px] backdrop-blur-xl ${
              isLight ? "bg-white/95 border-neutral-200" : "bg-[#0D0F12]/95 border-white/10"
            }`}
          >
            <div className="overflow-y-auto">
              {suggestions.map((s) => (
                <button
                  key={s.placeId}
                  type="button"
                  onClick={() => handleSelect(s)}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-colors flex items-start gap-3 ${
                    isLight ? "hover:bg-neutral-100 text-neutral-800 border-b border-neutral-50 last:border-0" : "hover:bg-white/5 text-neutral-100 border-b border-white/5 last:border-0"
                  }`}
                >
                  <MapPin size={16} className="mt-1 shrink-0 opacity-40" />
                  <div className="flex flex-col">
                    <span className="font-semibold text-sm leading-tight">{s.mainText}</span>
                    <span className="text-[11px] opacity-50 leading-relaxed mt-0.5">{s.secondaryText}</span>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};


export const FormContext = createContext({ showValidation: false });
export const useFormContext = () => useContext(FormContext);

export const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hour = Math.floor(i / 2).toString().padStart(2, '0');
  const min = (i % 2 === 0 ? '00' : '30');
  return `${hour}:${min}`;
});

// --- Helpers ---
const inputBase = (isLight) =>
  `w-full rounded-xl border px-3.5 py-2.5 text-sm outline-none transition shadow-sm ${
    isLight
      ? 'bg-white border-neutral-300 focus:border-neutral-900 focus:ring-1 focus:ring-neutral-900/40 placeholder:text-neutral-400 text-neutral-900 disabled:bg-neutral-50 disabled:text-neutral-400 disabled:border-neutral-200 disabled:cursor-not-allowed'
      : 'bg-[#05070A]/60 border-white/10 placeholder:text-neutral-500 focus:border-white focus:ring-1 focus:ring-white/50 text-neutral-100 disabled:bg-white/5 disabled:text-neutral-600 disabled:border-white/5 disabled:cursor-not-allowed'
  }`;

const labelBase = (isLight) =>
  `text-[10px] uppercase tracking-[0.18em] font-bold mb-1.5 block ${isLight ? "text-neutral-500" : "text-neutral-400"}`;

// --- Input ---
export const Input = ({ label, error, required, value, showValidation: propShowValidation, className = "", isLight = false, ...props }) => {
  const { showValidation: contextShowValidation } = useFormContext();
  const showValidation = propShowValidation || contextShowValidation;
  const isInvalid = error || (showValidation && required && (!value || (Array.isArray(value) ? value.length === 0 : (typeof value === 'string' && value.trim() === ""))));
  return (
    <div className={`flex flex-col w-full ${className}`}>
      {label && (
        <label className={labelBase(isLight)}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <input 
        className={`${inputBase(isLight)} ${isInvalid ? "border-red-500 bg-red-500/5 ring-2 ring-red-500/20" : ""} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`} 
        value={value}
        {...props} 
      />
      {isInvalid && <span className="text-[10px] font-bold text-red-500 mt-1.5 uppercase tracking-wider">{error || "This field is required"}</span>}
    </div>
  );
};

// --- Textarea ---
export const Textarea = ({ label, error, required, value, showValidation: propShowValidation, className = "", isLight = false, ...props }) => {
  const { showValidation: contextShowValidation } = useFormContext();
  const showValidation = propShowValidation || contextShowValidation;
  const isInvalid = error || (showValidation && required && (!value || (Array.isArray(value) ? value.length === 0 : (typeof value === 'string' && value.trim() === ""))));
  return (
    <div className={`flex flex-col w-full ${className}`}>
      {label && (
        <label className={labelBase(isLight)}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <textarea
        className={`${inputBase(isLight)} min-h-[100px] resize-y ${isInvalid ? "border-red-500 bg-red-500/5 ring-2 ring-red-500/20" : ""}`}
        value={value}
        {...props}
      />
      {isInvalid && <span className="text-[10px] font-bold text-red-500 mt-1.5 uppercase tracking-wider">{error || "This field is required"}</span>}
    </div>
  );
};

// --- Select ---
export const Select = ({ label, options, error, required, value, showValidation: propShowValidation, className = "", hidePlaceholder, isLight = false, ...props }) => {
  const { showValidation: contextShowValidation } = useFormContext();
  const showValidation = propShowValidation || contextShowValidation;
  const isInvalid = error || (showValidation && required && (!value || (Array.isArray(value) ? value.length === 0 : (typeof value === 'string' && value.trim() === ""))));
  return (
    <div className={`flex flex-col w-full ${className}`}>
      {label && (
        <label className={labelBase(isLight)}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        <select
          className={`w-full appearance-none ${inputBase(isLight)} pr-10 ${isInvalid ? "border-red-500 bg-red-500/5 ring-2 ring-red-500/20" : ""} ${!isLight ? '[&>option]:bg-[#0D0F12] [&>option]:text-white' : ''}`}
          value={value}
          {...props}
        >
          {!hidePlaceholder && <option value="" className={!isLight ? "bg-[#0D0F12] text-white" : ""}>Select an option</option>}
          {options.map((opt) => (
            <option key={opt} value={opt} className={!isLight ? "bg-[#0D0F12] text-white" : ""}>{opt}</option>
          ))}
        </select>
        <ChevronDown className={`absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${isLight ? "text-neutral-400" : "text-neutral-500"}`} />
      </div>
      {isInvalid && <span className="text-[10px] font-bold text-red-500 mt-1.5 uppercase tracking-wider">{error || "This field is required"}</span>}
    </div>
  );
};

// --- TimePicker ---
export const TimePicker = ({ label, value, onChange, isLight = false, required, showValidation: propShowValidation, className = "" }) => {
  const { showValidation: contextShowValidation } = useFormContext();
  const showValidation = propShowValidation || contextShowValidation;
  const [isOpen, setIsOpen] = React.useState(false);
  const [dropdownPos, setDropdownPos] = React.useState({ top: 0, left: 0 });
  const containerRef = React.useRef(null);
  const buttonRef = React.useRef(null);
  const dropdownRef = React.useRef(null);
  const isInvalid = showValidation && required && !value;
  
  const parts = (value || "00:00").split(":");
  const h = parts[0] || "00";
  const m = parts[1] || "00";

  const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, "0"));
  const minutes = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current && !containerRef.current.contains(event.target) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Recalculate position when opening or on scroll/resize
  React.useEffect(() => {
    if (!isOpen || !buttonRef.current) return;
    const updatePos = () => {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    };
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [isOpen]);

  return (
    <div className={`flex flex-col ${className}`} ref={containerRef}>
      {label && <label className={`${labelBase(isLight)} whitespace-nowrap`}>{label} {required && <span className="text-red-500">*</span>}</label>}
      
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{ maxWidth: '120px' }}
        className={`flex items-center justify-between w-full rounded-xl border transition-all shadow-sm px-3 py-2.5 text-sm ${
          isLight 
            ? "bg-white border-neutral-300 text-neutral-900" 
            : "bg-[#05070A]/60 border-white/10 text-neutral-100"
        } ${isOpen ? (isLight ? "border-neutral-900 ring-1 ring-neutral-900/40" : "border-white ring-1 ring-white/50") : ""} ${isInvalid ? "border-red-500 ring-1 ring-red-500/20" : ""}`}
      >
        <span className="font-medium tracking-wide">{h}:{m}</span>
        <ChevronDown size={14} className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""} ${isLight ? "text-neutral-400" : "text-neutral-500"}`} />
      </button>
      {isInvalid && <span className="text-[10px] font-bold text-red-500 mt-1.5 uppercase tracking-wider">This field is required</span>}

      {isOpen && ReactDOM.createPortal(
        <motion.div
          ref={dropdownRef}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className={`fixed w-48 rounded-2xl border shadow-2xl overflow-hidden flex ${
            isLight ? "bg-white border-neutral-200" : "bg-[#0D0F12] border-white/10"
          }`}
          style={{ top: dropdownPos.top, left: dropdownPos.left, maxHeight: "280px", zIndex: 99999 }}
        >
          {/* Hours Column */}
          <div className={`flex-1 overflow-y-auto scrollbar-hide border-r ${isLight ? "border-neutral-100" : "border-white/5"}`}>
            <div className={`px-3 py-2 text-[10px] font-bold uppercase tracking-wider sticky top-0 z-10 ${isLight ? "bg-white text-neutral-400" : "bg-[#0D0F12] text-neutral-500"}`}>Hr</div>
            {hours.map((hour) => (
              <button
                key={hour}
                type="button"
                onClick={() => onChange(`${hour}:${m}`)}
                className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                  h === hour 
                    ? (isLight ? "bg-neutral-900 text-white" : "bg-white text-black font-bold") 
                    : (isLight ? "hover:bg-neutral-50 text-neutral-600" : "hover:bg-white/5 text-neutral-400")
                }`}
              >
                {hour}
              </button>
            ))}
          </div>

          {/* Minutes Column */}
          <div className="flex-1 overflow-y-auto scrollbar-hide">
            <div className={`px-3 py-2 text-[10px] font-bold uppercase tracking-wider sticky top-0 z-10 ${isLight ? "bg-white text-neutral-400" : "bg-[#0D0F12] text-neutral-500"}`}>Min</div>
            {minutes.map((min) => (
              <button
                key={min}
                type="button"
                onClick={() => {
                  onChange(`${h}:${min}`);
                }}
                className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                  m === min 
                    ? (isLight ? "bg-neutral-900 text-white" : "bg-white text-black font-bold") 
                    : (isLight ? "hover:bg-neutral-50 text-neutral-600" : "hover:bg-white/5 text-neutral-400")
                }`}
              >
                {min}
              </button>
            ))}
          </div>
        </motion.div>,
        document.body
      )}
    </div>
  );
};

// --- Toggle ---
export const Toggle = ({ label, subtitle, value, onChange, children, plain, isLight = false, disabled = false }) => (
  <div className={`flex flex-col gap-4 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
    <div className="flex items-center justify-between gap-4">
      <div className="flex flex-col">
        <span className={`text-sm font-semibold tracking-tight ${isLight ? "text-neutral-900" : "text-neutral-100"}`}>{label}</span>
        {subtitle && <p className={`text-[10px] uppercase tracking-wider mt-0.5 ${isLight ? "text-neutral-400" : "text-neutral-500"}`}>{subtitle}</p>}
      </div>
      <button
        type="button"
        onClick={() => !disabled && onChange(!value)}
        className={`relative w-[60px] h-7 rounded-full transition-all duration-300 focus:outline-none flex items-center flex-shrink-0 px-1.5 ${
          value 
            ? "bg-blue-500 shadow-[0_0_12px_rgba(59,130,246,0.4)]" 
            : isLight ? "bg-neutral-200" : "bg-white/10"
        } ${disabled ? 'cursor-not-allowed' : ''}`}
      >
        <span className={`absolute left-7 text-[9px] font-black tracking-tighter transition-opacity duration-200 ${value ? "opacity-0" : isLight ? "text-neutral-400" : "text-neutral-500"}`}>
          NO
        </span>
        <span className={`absolute left-2.5 text-[9px] font-black tracking-tighter transition-opacity duration-200 ${value ? "text-white opacity-100" : "opacity-0"}`}>
          YES
        </span>
        <motion.div
          animate={{ x: value ? 32 : 0 }}
          className="w-5 h-5 bg-white rounded-full shadow-md z-10"
          transition={{ type: "spring", stiffness: 500, damping: 30 }}
        />
      </button>
    </div>
    <AnimatePresence>
      {value && children && (
        <motion.div
          initial={{ height: 0, opacity: 0, overflow: "hidden" }}
          animate={{ height: "auto", opacity: 1, transitionEnd: { overflow: "visible" } }}
          exit={{ height: 0, opacity: 0, overflow: "hidden" }}
        >
          {plain ? (
            <div className="pt-2 pb-4 flex flex-col gap-6 relative z-10">{children}</div>
          ) : (
            <div className={`pt-2 pb-4 flex flex-col gap-6 border-l-2 pl-6 ml-2 relative z-10 ${isLight ? "border-neutral-200" : "border-white/5"}`}>{children}</div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

// --- PillGroup (Single Select) ---
export const PillGroup = ({ label, options, value, onChange, required, showValidation: propShowValidation, isLight = false }) => {
  const { showValidation: contextShowValidation } = useFormContext();
  const showValidation = propShowValidation || contextShowValidation;
  const isInvalid = showValidation && required && (!value || (typeof value === 'string' && value.trim() === ""));
  return (
    <div className="flex flex-col">
      {label && (
        <label className={labelBase(isLight)}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className={`flex flex-wrap gap-2 mt-1 p-2 rounded-2xl transition-all ${isInvalid ? "bg-red-500/5 ring-1 ring-red-500/20" : ""}`}>
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(value === opt ? "" : opt)}
            className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all border ${
              value === opt
                ? isLight
                  ? "bg-neutral-900 text-white border-neutral-900 shadow-[0_8px_20px_rgba(15,23,42,0.3)]"
                  : "bg-white text-black border-white shadow-[0_8px_20px_rgba(255,255,255,0.1)]"
                : isLight
                  ? "bg-white text-neutral-500 border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50"
                  : "bg-white/5 text-neutral-400 border-white/5 hover:border-white/15 hover:bg-white/10"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
      {isInvalid && <span className="text-[10px] font-bold text-red-500 mt-1.5 uppercase tracking-wider">Please select an option</span>}
    </div>
  );
};

export const TagGroup = ({ label, subtitle, options, value = [], onChange, required, error, showValidation: propShowValidation, isLight = false }) => {
  const { showValidation: contextShowValidation } = useFormContext();
  const showValidation = propShowValidation || contextShowValidation;
  const isInvalid = error || (showValidation && required && (!value || value.length === 0));
  return (
    <div className="flex flex-col">
      <div className="mb-2">
        <label className={labelBase(isLight)}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        {subtitle && <p className={`text-[10px] uppercase tracking-wider ${isLight ? "text-neutral-400" : "text-neutral-500"}`}>{subtitle}</p>}
      </div>
      <div className={`flex flex-wrap gap-2 p-2 rounded-2xl transition-all ${isInvalid ? "bg-red-500/5 ring-1 ring-red-500/20" : ""}`}>
        {options.map((opt) => {
          const isActive = value.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => {
                if (isActive) onChange(value.filter((v) => v !== opt));
                else onChange([...value, opt]);
              }}
              className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all border flex items-center gap-2 ${
                isActive
                  ? isLight
                    ? "bg-neutral-900 text-white border-neutral-900 shadow-[0_8px_20px_rgba(15,23,42,0.3)]"
                    : "bg-white text-black border-white shadow-[0_8px_20px_rgba(255,255,255,0.1)]"
                  : isLight
                    ? "bg-white text-neutral-500 border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50"
                    : "bg-white/5 text-neutral-400 border-white/5 hover:border-white/15 hover:bg-white/10"
              }`}
            >
              {opt}
              {isActive && <Check size={12} />}
            </button>
          );
        })}
      </div>
      {isInvalid && <span className="text-[10px] font-bold text-red-500 mt-1.5 uppercase tracking-wider">{error || "Please select at least one option"}</span>}
    </div>
  );
};

// --- RepeatableGroup ---
export const RepeatableGroup = ({ label, items, onAdd, onRemove, renderItem, required, showValidation: propShowValidation, error, isLight = false }) => {
  const { showValidation: contextShowValidation } = useFormContext();
  const showValidation = propShowValidation || contextShowValidation;
  const [expandedIndex, setExpandedIndex] = useState(items.length - 1);
  const isInvalid = showValidation && required && items.length === 0;

  useEffect(() => {
    if (items.length > 0 && expandedIndex === -1) {
      setExpandedIndex(items.length - 1);
    }
  }, [items.length]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <label className={labelBase(isLight)}>{label}</label>
        <button
          type="button"
          onClick={() => {
            onAdd();
            setExpandedIndex(items.length);
          }}
          className="flex items-center gap-1.5 text-[10px] font-black tracking-widest text-blue-400 hover:text-blue-300 transition-all active:scale-95"
        >
          <Plus className="w-3.5 h-3.5" /> ADD NEW
        </button>
      </div>
      <div className="flex flex-col gap-4">
        {items.map((item, index) => {
          const isExpanded = expandedIndex === index;
          const displayTitle = item.name || `${label.slice(0, -1)} #${index + 1}`;

          return (
            <div key={index} className={`relative rounded-3xl border transition-all duration-300 overflow-hidden ${
              isExpanded 
                ? isLight ? "bg-white border-neutral-200 shadow-xl" : "bg-white/[0.03] border-white/10 shadow-2xl" 
                : isLight ? "bg-neutral-50/50 border-neutral-100 hover:bg-white" : "bg-white/5 border-white/5 hover:bg-white/[0.08]"
            } ${isInvalid ? "border-red-500 bg-red-500/5" : ""}`}>
              <button
                type="button"
                onClick={() => setExpandedIndex(isExpanded ? -1 : index)}
                className="w-full flex items-center justify-between px-6 py-4 text-left"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-bold ${
                    isExpanded 
                      ? "bg-blue-500 text-white" 
                      : isLight ? "bg-neutral-200 text-neutral-500" : "bg-white/10 text-neutral-400"
                  }`}>
                    {index + 1}
                  </div>
                  <span className={`text-sm font-bold tracking-tight ${isLight ? "text-neutral-900" : "text-neutral-100"}`}>
                    {displayTitle}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onRemove(index); }}
                    className={`p-2 rounded-xl transition-colors ${isLight ? "text-neutral-400 hover:bg-red-50 hover:text-red-500" : "text-neutral-500 hover:bg-red-500/10 hover:text-red-400"}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <ChevronDown size={18} className={`transition-transform duration-300 ${isExpanded ? "rotate-180" : ""} ${isLight ? "text-neutral-400" : "text-neutral-500"}`} />
                </div>
              </button>

              <AnimatePresence initial={false}>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ type: "spring", duration: 0.4, bounce: 0 }}
                  >
                    <div className={`px-6 pb-6 pt-2 border-t ${isLight ? "border-neutral-100" : "border-white/5"}`}>
                      {renderItem(item, index)}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
        <AnimatePresence>
          {isInvalid && (
            <motion.span 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-[10px] font-bold text-red-500 mt-1.5 uppercase tracking-wider"
            >
              {error || `Please add at least one ${label.toLowerCase()}`}
            </motion.span>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// --- MultiSelect (Searchable) ---
export const MultiSelect = ({ label, options, value, onChange, required, placeholder = "Search...", isLight = false, hideSearch = false }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = hideSearch
    ? options.filter((opt) => !value.includes(opt))
    : options.filter((opt) => opt.toLowerCase().includes(search.toLowerCase()) && !value.includes(opt));

  const toggleOption = (opt) => {
    if (value.includes(opt)) onChange(value.filter((v) => v !== opt));
    else onChange([...value, opt]);
  };

  return (
    <div className="flex flex-col w-full relative" ref={containerRef}>
      <label className={labelBase(isLight)}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div
        className={`w-full rounded-xl border min-h-[44px] px-3.5 py-2 text-sm transition shadow-sm cursor-pointer flex flex-wrap gap-1.5 items-center ${
          isLight 
            ? "bg-white border-neutral-300 focus-within:border-neutral-900 focus-within:ring-1 focus-within:ring-neutral-900/40 text-neutral-900" 
            : "bg-[#05070A]/60 border-white/10 focus-within:border-white focus-within:ring-1 focus-within:ring-white/50 text-neutral-100"
        } ${isOpen ? (isLight ? "border-neutral-900 ring-1 ring-neutral-900/40" : "border-white ring-1 ring-white/50") : ""}`}
        onClick={() => setIsOpen(true)}
      >
        {value.length === 0 && !isOpen && (
          <span className={`${isLight ? "text-neutral-400" : "text-neutral-500"}`}>Select an option</span>
        )}
        {value.map((val) => (
          <span key={val} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border flex items-center gap-1.5 transition-all ${isLight ? "bg-neutral-100 text-neutral-800 border-neutral-200" : "bg-white/10 text-neutral-100 border-white/10"}`}>
            {val}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); toggleOption(val); }}
              className="hover:text-red-400 transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <ChevronDown className={`ml-auto w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''} ${isLight ? "text-neutral-400" : "text-neutral-500"}`} />
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.1 }}
            className={`absolute top-full left-0 right-0 mt-1 rounded-md shadow-xl border py-1 z-50 overflow-hidden flex flex-col max-h-[300px] ${
              isLight ? "bg-white border-neutral-300" : "bg-[#202020] border-[#333]"
            }`}
          >
            {!hideSearch && (
              <div className={`p-2 relative`}>
                <Search className={`absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 ${isLight ? "text-neutral-400" : "text-neutral-500"}`} />
                <input
                  autoFocus
                  type="text"
                  placeholder={placeholder}
                  className={`w-full border pl-10 pr-3 py-1.5 rounded text-sm outline-none ${
                    isLight ? "bg-neutral-50 border-neutral-300 text-neutral-900 focus:border-neutral-500" : "bg-[#333] border-[#444] text-neutral-100 focus:border-[#555]"
                  }`}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
            <div className={`overflow-y-auto flex-1 ${isLight ? 'custom-scroll-light' : 'custom-scroll'}`}>
              {filteredOptions.length === 0 ? (
                <div className={`p-4 text-center text-xs text-neutral-500`}>No results found</div>
              ) : (
                filteredOptions.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className={`w-full text-left px-3 py-1.5 text-sm transition-none ${
                      isLight
                        ? "text-neutral-900 hover:bg-neutral-200"
                        : "text-neutral-200 hover:bg-[#3f3f46] hover:text-white"
                    }`}
                    onClick={(e) => { e.stopPropagation(); toggleOption(opt); setSearch(""); }}
                  >
                    {opt}
                  </button>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- Stepper ---
export const Stepper = ({ label, value, onChange, required, isLight = false, showValidation: propShowValidation }) => {
  const { showValidation: contextShowValidation } = useFormContext();
  const showValidation = propShowValidation || contextShowValidation;
  const numValue = typeof value === "number" ? value : 0;
  const isInvalid = showValidation && required && numValue <= 0;

  return (
    <div className="flex flex-col w-full">
      <label className={labelBase(isLight)}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className={`flex items-center gap-4 border px-4 py-2 rounded-xl h-[50px] justify-between transition-all ${
        isInvalid 
          ? "border-red-500 bg-red-500/5 ring-1 ring-red-500/20" 
          : isLight ? "bg-white border-neutral-300" : "bg-[#05070A]/60 border-white/10"
      }`}>
        <button
          type="button"
          onClick={() => onChange(Math.max(0, numValue - 1))}
          className={`w-8 h-8 rounded-full flex items-center justify-center transition-all active:scale-90 ${isLight ? "bg-neutral-100 text-neutral-600 hover:bg-neutral-200" : "bg-white/10 text-neutral-300 hover:bg-white/20"}`}
        >
          <Minus className="w-4 h-4" />
        </button>
        <span className={`font-bold text-lg ${isLight ? "text-neutral-900" : "text-neutral-100"}`}>{numValue}</span>
        <button
          type="button"
          onClick={() => onChange(numValue + 1)}
          className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center hover:bg-blue-400 transition-all shadow-lg shadow-blue-500/20 active:scale-90"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      {isInvalid && <span className="text-[10px] font-bold text-red-500 mt-1.5 uppercase tracking-wider">This field is required</span>}
    </div>
  );
};

// --- Card ---
export const Card = ({ title, icon: Icon, children, subtitle, isLight = false }) => (
  <div className={`rounded-3xl backdrop-blur-2xl border p-5 sm:p-8 flex flex-col gap-6 transition-all duration-300 relative focus-within:z-50 ${
    isLight
      ? 'bg-white border-neutral-200 shadow-[0_18px_40px_rgba(15,23,42,0.12)]'
      : 'bg-white/5 border-white/5 shadow-[0_24px_70px_rgba(0,0,0,0.75)]'
  }`}>
    <div className={`flex items-center gap-4 border-b pb-6 ${isLight ? "border-neutral-100" : "border-white/5"}`}>
      <div className={`h-10 w-10 rounded-2xl flex items-center justify-center border transition-all ${
        isLight ? 'bg-neutral-50 border-neutral-200 text-neutral-600' : 'bg-white/5 border-white/15 text-neutral-300'
      }`}>
        <Icon size={20} />
      </div>
      <div>
        <h3 className={`text-base font-semibold tracking-tight ${isLight ? "text-neutral-900" : "text-neutral-100"}`}>{title}</h3>
        {subtitle && <p className={`text-[10px] uppercase tracking-[0.15em] font-bold mt-0.5 ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>{subtitle}</p>}
      </div>
    </div>
    <div className="flex flex-col gap-8">{children}</div>
  </div>
);
